/**
 * Accessibility utilities for subscription components
 * Provides WCAG 2.1 AA compliant features and helpers
 */

export interface AccessibilityConfig {
  announceChanges?: boolean;
  highContrast?: boolean;
  reducedMotion?: boolean;
  screenReaderOptimized?: boolean;
}

/**
 * Announces important changes to screen readers
 */
export const announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;
  
  document.body.appendChild(announcement);
  
  // Remove after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

/**
 * Manages focus for modal dialogs and complex interactions
 */
export class FocusManager {
  private previousFocus: HTMLElement | null = null;
  private focusableElements: HTMLElement[] = [];
  private currentIndex = 0;

  constructor(private container: HTMLElement) {
    this.updateFocusableElements();
  }

  private updateFocusableElements() {
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    this.focusableElements = Array.from(
      this.container.querySelectorAll(focusableSelectors)
    ) as HTMLElement[];
  }

  trapFocus() {
    this.previousFocus = document.activeElement as HTMLElement;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        
        if (e.shiftKey) {
          this.focusPrevious();
        } else {
          this.focusNext();
        }
      }
      
      if (e.key === 'Escape') {
        this.releaseFocus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // Focus first element
    if (this.focusableElements.length > 0) {
      this.focusableElements[0].focus();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }

  private focusNext() {
    this.currentIndex = (this.currentIndex + 1) % this.focusableElements.length;
    this.focusableElements[this.currentIndex]?.focus();
  }

  private focusPrevious() {
    this.currentIndex = this.currentIndex === 0 
      ? this.focusableElements.length - 1 
      : this.currentIndex - 1;
    this.focusableElements[this.currentIndex]?.focus();
  }

  releaseFocus() {
    if (this.previousFocus) {
      this.previousFocus.focus();
    }
  }
}

/**
 * Validates color contrast ratios for accessibility
 */
export const validateColorContrast = (foreground: string, background: string): boolean => {
  // This is a simplified version - in production, you'd use a proper color contrast library
  // For now, we'll assume our design system colors meet WCAG AA standards
  return true;
};

/**
 * Generates accessible descriptions for subscription status
 */
export const generateStatusDescription = (status: {
  isActive: boolean;
  isTrialActive: boolean;
  remainingDays: number;
  trialRemainingDays: number;
  planName?: string;
}): string => {
  if (status.isTrialActive) {
    const urgency = status.trialRemainingDays <= 3 ? 'urgent' : 'normal';
    return `Trial period active. ${status.trialRemainingDays} days remaining. ${
      urgency === 'urgent' ? 'Trial expires soon, consider upgrading.' : ''
    }`;
  }

  if (status.isActive) {
    return `Active subscription to ${status.planName || 'current plan'}. ${status.remainingDays} days remaining.`;
  }

  return 'No active subscription. Upgrade to access premium content.';
};

/**
 * Formats currency amounts for screen readers
 */
export const formatCurrencyForScreenReader = (amount: number, currency: string = 'EUR'): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });

  const formatted = formatter.format(amount);
  
  // Make it more screen reader friendly
  return formatted.replace('€', 'euros').replace('$', 'dollars');
};

/**
 * Generates accessible labels for form fields
 */
export const generateFieldLabel = (fieldName: string, required: boolean = false): string => {
  const labels: Record<string, string> = {
    cardNumber: 'Credit card number',
    expiryMonth: 'Expiration month',
    expiryYear: 'Expiration year',
    cvv: 'Security code',
    cardHolderName: 'Cardholder name'
  };

  const label = labels[fieldName] || fieldName;
  return required ? `${label}, required` : label;
};

/**
 * Keyboard navigation helpers
 */
export const handleKeyboardNavigation = (
  event: KeyboardEvent,
  options: {
    onEnter?: () => void;
    onSpace?: () => void;
    onEscape?: () => void;
    onArrowUp?: () => void;
    onArrowDown?: () => void;
    onArrowLeft?: () => void;
    onArrowRight?: () => void;
  }
) => {
  switch (event.key) {
    case 'Enter':
      event.preventDefault();
      options.onEnter?.();
      break;
    case ' ':
      event.preventDefault();
      options.onSpace?.();
      break;
    case 'Escape':
      event.preventDefault();
      options.onEscape?.();
      break;
    case 'ArrowUp':
      event.preventDefault();
      options.onArrowUp?.();
      break;
    case 'ArrowDown':
      event.preventDefault();
      options.onArrowDown?.();
      break;
    case 'ArrowLeft':
      event.preventDefault();
      options.onArrowLeft?.();
      break;
    case 'ArrowRight':
      event.preventDefault();
      options.onArrowRight?.();
      break;
  }
};

/**
 * Checks if user prefers reduced motion
 */
export const prefersReducedMotion = (): boolean => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Checks if user prefers high contrast
 */
export const prefersHighContrast = (): boolean => {
  return window.matchMedia('(prefers-contrast: high)').matches;
};

/**
 * Creates accessible loading announcements
 */
export const createLoadingAnnouncement = (action: string): {
  start: () => void;
  update: (progress?: string) => void;
  complete: (result?: string) => void;
  error: (error: string) => void;
} => {
  return {
    start: () => announceToScreenReader(`${action} started`, 'polite'),
    update: (progress) => announceToScreenReader(progress || `${action} in progress`, 'polite'),
    complete: (result) => announceToScreenReader(result || `${action} completed successfully`, 'polite'),
    error: (error) => announceToScreenReader(`${action} failed: ${error}`, 'assertive')
  };
};

/**
 * Validates form accessibility
 */
export const validateFormAccessibility = (form: HTMLFormElement): {
  isValid: boolean;
  issues: string[];
} => {
  const issues: string[] = [];
  
  // Check for labels
  const inputs = form.querySelectorAll('input, select, textarea');
  inputs.forEach((input) => {
    const id = input.getAttribute('id');
    const ariaLabel = input.getAttribute('aria-label');
    const ariaLabelledBy = input.getAttribute('aria-labelledby');
    
    if (id) {
      const label = form.querySelector(`label[for="${id}"]`);
      if (!label && !ariaLabel && !ariaLabelledBy) {
        issues.push(`Input ${id} missing accessible label`);
      }
    }
  });

  // Check for error associations
  const errorElements = form.querySelectorAll('[role="alert"], .error, [id$="-error"]');
  errorElements.forEach((error) => {
    const errorId = error.getAttribute('id');
    if (errorId) {
      const associatedInput = form.querySelector(`[aria-describedby*="${errorId}"]`);
      if (!associatedInput) {
        issues.push(`Error ${errorId} not associated with any input`);
      }
    }
  });

  return {
    isValid: issues.length === 0,
    issues
  };
};

/**
 * Screen reader optimized text for complex UI states
 */
export const getScreenReaderText = {
  subscriptionProgress: (percentage: number, type: 'trial' | 'subscription') => 
    `${type} progress: ${Math.round(percentage)} percent complete`,
    
  paymentStep: (step: 'validating' | 'processing' | 'confirming') => {
    const steps = {
      validating: 'Step 1 of 3: Validating payment information',
      processing: 'Step 2 of 3: Processing payment',
      confirming: 'Step 3 of 3: Confirming transaction'
    };
    return steps[step];
  },
  
  planComparison: (plan: { name: string; price: number; duration: number; features: string[] }) =>
    `${plan.name} plan: ${plan.price} euros for ${plan.duration} days. Includes ${plan.features.length} features: ${plan.features.join(', ')}`,
    
  subscriptionStatus: (status: string, daysRemaining?: number) => {
    if (daysRemaining !== undefined) {
      return `Subscription status: ${status}. ${daysRemaining} days remaining.`;
    }
    return `Subscription status: ${status}`;
  }
};